import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import DashboardHeader from '../../components/DashboardHeader';
import { useAuth } from '../../context/AuthContext';
import {
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaList,
  FaTh,
  FaUser,
  FaHospital,
  FaCalendarAlt,
  FaClock
} from 'react-icons/fa';
import '../../App.css';
import client from '../../api/client';
import { toast } from 'react-toastify';

function TimeSheetApproval() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTimeSheet, setSelectedTimeSheet] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [timeSheets, setTimeSheets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [timeSheetToReject, setTimeSheetToReject] = useState(null);
  const [isRejectingOrApproving, setIsRejectingOrApproving] = useState(false);

  // Format date for display (DD MMM YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    const date = new Date(dateTimeString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatINRNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(String(value).replace(/,/g, ''));
    if (Number.isNaN(num)) return '';

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // ---- NEW HELPERS (shift / period + planned/actual dates) ----
  const getJobTypeLabel = (jobDurationType) => {
    const t = (jobDurationType || '').toLowerCase();
    if (t.includes('period')) return 'Period';
    if (t.includes('shift')) return 'Shift';
    return jobDurationType || '-';
  };

  // Planned dates: for period jobs use dateFrom/dateTo, else fallback to startDateTime
  const getPlannedStart = (ts) => ts?.dateFrom || ts?.startDateTime || null;
  const getPlannedEnd = (ts) => ts?.dateTo || null;

  // Actual dates: your API provides startDateTime + actualWorkCompleteDateTime
  const getActualStart = (ts) => ts?.startDateTime || null;
  const getActualEnd = (ts) => ts?.actualWorkCompleteDateTime || null;

  // Fetch timesheet list from API
  const loadTimeSheets = async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await client.get('/api/healthcare-professionals/timesheets');
      const list = response.data?.data || [];

      // Normalize to internal shape (IMPORTANT: keep dateFrom/dateTo + actualWorkCompleteDateTime)
      const normalized = list.map((item, idx) => ({
        id: item.jobId ?? idx + 1,
        jobId: item.jobId ?? null,
        jobApplicationId: item.jobApplicationId ?? null,
        professionalName: item.professionalName || '',
        hospitalName: item.hospitalName || '',
        posting: item.posting || '',
        jobDurationType: item.jobDurationType || '',
        remarksByProfessional: item.remarksByProfessional || '',

        // planned period range
        dateFrom: item.dateFrom || null,
        dateTo: item.dateTo || null,

        // actual dates
        startDateTime: item.startDateTime || null, // treat as actual start
        actualWorkCompleteDateTime: item.actualWorkCompleteDateTime || null, // actual end

        duration: item.duration ?? null,
        totalHours: item.totalHours ?? null,
        totalAmount: item.totalAmount ?? null,
        submittedDate: item.submittedDate || null,
        status: item.status || 'Pending',
        details: item.details || []
      }));

      setTimeSheets(normalized);
    } catch (err) {
      setLoadError(err?.response?.data?.message || 'Unable to load time sheets.');
      setTimeSheets([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTimeSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  // Get filtered time sheets
  const getFilteredTimeSheets = () => {
    let filtered = timeSheets;

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(
        (ts) => (ts.status || '').toLowerCase() === filterStatus.toLowerCase()
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (ts) =>
          (ts.professionalName || '').toLowerCase().includes(term) ||
          (ts.hospitalName || '').toLowerCase().includes(term) ||
          (ts.posting || '').toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  // Handle view details
  const handleViewDetails = (timeSheetId) => {
    const timeSheet = timeSheets.find((ts) => ts.id === timeSheetId);
    if (timeSheet) {
      setSelectedTimeSheet(timeSheet);
      setShowDetailsModal(true);
    }
  };

  // Handle close details modal
  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedTimeSheet(null);
  };

  // Approve timesheet via API
  const handleApprove = async (timeSheet) => {
    if (!timeSheet?.jobId) {
      toast.error('Unable to approve: missing jobId.');
      return;
    }

    if (!window.confirm('Are you sure you want to approve this time sheet?')) return;

    setIsRejectingOrApproving(true);
    try {
      const payload = {
        jobId: timeSheet.jobId,
        jobApplicationId: timeSheet.jobApplicationId,
        status: 'Approved',
        remarks: null
      };

      await client.put('/api/healthcare-professionals/timesheet/status', payload);
      toast.success('Time sheet approved successfully!');
      await loadTimeSheets();
      handleCloseDetailsModal();
    } catch (err) {
      console.error('Approve timesheet error:', err);
      toast.error(err?.response?.data?.message || 'Failed to approve time sheet.');
    } finally {
      setIsRejectingOrApproving(false);
    }
  };

  // Open reject modal
  const handleOpenRejectModal = (timeSheet) => {
    if (!timeSheet?.jobId) {
      toast.error('Unable to reject: missing jobId.');
      return;
    }
    setTimeSheetToReject(timeSheet);
    setRejectRemarks('');
    setShowRejectModal(true);
  };

  // Close reject modal
  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setRejectRemarks('');
    setTimeSheetToReject(null);
  };

  // Confirm rejection with remarks
  const handleConfirmReject = async () => {
    if (!rejectRemarks.trim()) {
      toast.error('Remarks are mandatory for rejection.');
      return;
    }

    setIsRejectingOrApproving(true);
    try {
      const payload = {
        jobId: timeSheetToReject.jobId,
        jobApplicationId: timeSheetToReject.jobApplicationId,
        status: 'Rejected',
        remarks: rejectRemarks.trim()
      };

      await client.put('/api/healthcare-professionals/timesheet/status', payload);
      toast.success('Time sheet rejected successfully!');
      await loadTimeSheets();
      handleCloseRejectModal();
      handleCloseDetailsModal();
    } catch (err) {
      console.error('Reject timesheet error:', err);
      toast.error(err?.response?.data?.message || 'Failed to reject time sheet.');
    } finally {
      setIsRejectingOrApproving(false);
    }
  };

  const filteredTimeSheets = getFilteredTimeSheets();

  const renderStatusBadge = (status) => {
    if (status === 'Submitted') {
      return <span className="badge bg-warning text-dark p-2 px-3">Submitted</span>;
    }
    if (status === 'Approved') {
      return <span className="badge bg-success p-2 px-3">Approved</span>;
    }
    if (status === 'Rejected') {
      return <span className="badge bg-danger p-2 px-3">Rejected</span>;
    }
    return <span className="badge bg-secondary p-2 px-3">{status || 'Pending'}</span>;
  };

  // Column count in table = 15 (keep this for colSpan)
  const TABLE_COLS = 15;

  return (
    <div className="App">
      <DashboardHeader userType="admin" userName={user?.displayName || 'Admin'} />

      <main className="dashboard-content">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="d-block d-md-flex justify-content-between align-items-center mb-4">
                <h2 className="dashboard-title mb-0">Time Sheet Approval</h2>
                <div className="d-block d-md-flex justify-content-between align-items-center">
                  <div className="btn-group me-3" role="group" aria-label="View Toggle">
                    <button
                      type="button"
                      className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('table')}
                      title="Table View"
                    >
                      <FaList />
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('grid')}
                      title="Grid View"
                    >
                      <FaTh />
                    </button>
                  </div>
                </div>
              </div>

              {/* Search and Filter Section */}
              <div className="mb-4">
                <div className="row">
                  <div className="col-md-3 mb-3 mb-md-0">
                    <label htmlFor="search" className="form-label">
                      Search
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <FaSearch />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Professional, Hospital, or Posting"
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3 mb-md-0">
                    <label htmlFor="statusFilter" className="form-label">
                      Filter by Status
                    </label>
                    <div className="input-group">
                      <select
                        className="form-select"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="Submitted">Submitted</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Time Sheets Table View */}
              {viewMode === 'table' && (
                <div className="card">
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">#</th>
                            <th scope="col">Professional</th>
                            <th scope="col">Hospital</th>
                            <th scope="col">Posting</th>

                            {/* NEW */}
                            <th scope="col">Job Type</th>
                            <th scope="col">Start Date</th>
                            <th scope="col">End Date</th>
                            <th scope="col">Actual Start</th>
                            <th scope="col">Actual End</th>

                            <th scope="col">Duration</th>
                            <th scope="col" className="text-center">
                              Total Hours
                            </th>
                            <th scope="col">Total Amount</th>
                            <th scope="col">Submitted Date</th>
                            <th scope="col">Status</th>
                            <th scope="col">Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan={TABLE_COLS} className="text-center">
                                Loading...
                              </td>
                            </tr>
                          ) : loadError ? (
                            <tr>
                              <td colSpan={TABLE_COLS} className="text-center text-danger">
                                {loadError}
                              </td>
                            </tr>
                          ) : filteredTimeSheets.length === 0 ? (
                            <tr>
                              <td colSpan={TABLE_COLS} className="text-center">
                                No time sheets found
                              </td>
                            </tr>
                          ) : (
                            filteredTimeSheets.map((timeSheet, index) => {
                              const jobType = getJobTypeLabel(timeSheet.jobDurationType);
                              const plannedStart = getPlannedStart(timeSheet);
                              const plannedEnd = getPlannedEnd(timeSheet);
                              const actualStart = getActualStart(timeSheet);
                              const actualEnd = getActualEnd(timeSheet);

                              return (
                                <tr key={timeSheet.id}>
                                  <td>{index + 1}</td>

                                  <td>
                                    <div className="d-flex align-items-center">{timeSheet.professionalName}</div>
                                  </td>

                                  <td>
                                    <div className="d-flex align-items-center">{timeSheet.hospitalName}</div>
                                  </td>

                                  <td>{timeSheet.posting}</td>

                                  {/* NEW */}
                                  <td>{jobType}</td>

                                  <td>
                                    {formatDate(plannedStart)}
                                    {jobType === 'Shift' && plannedStart ? (
                                      <>
                                        <br />
                                        {formatTime(plannedStart)}
                                      </>
                                    ) : null}
                                  </td>

                                  <td>{formatDate(plannedEnd)}</td>

                                  <td>
                                    {formatDate(actualStart)}
                                    {jobType === 'Shift' && actualStart ? (
                                      <>
                                        <br />
                                        {formatTime(actualStart)}
                                      </>
                                    ) : null}
                                  </td>

                                  <td>
                                    {formatDate(actualEnd)}
                                    {jobType === 'Shift' && actualEnd ? (
                                      <>
                                        <br />
                                        {formatTime(actualEnd)}
                                      </>
                                    ) : null}
                                  </td>

                                  <td>
                                    {jobType === 'Period'
                                      ? '-'
                                      : `${timeSheet.duration ?? '-'} hrs`}
                                  </td>

                                  <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center">
                                      {timeSheet.totalHours ?? '-'} {timeSheet.totalHours != null ? 'hrs' : ''}
                                    </div>
                                  </td>

                                  <td className="text-end">
                                    <div className="d-flex align-items-center">
                                      {timeSheet.totalAmount !== null &&
                                      timeSheet.totalAmount !== undefined &&
                                      timeSheet.totalAmount !== ''
                                        ? `Rs. ${formatINRNumber(timeSheet.totalAmount)}`
                                        : '-'}
                                    </div>
                                  </td>

                                  <td>{formatDate(timeSheet.submittedDate)}</td>

                                  <td>{renderStatusBadge(timeSheet.status)}</td>

                                  <td>
                                    <div className="d-flex gap-1">
                                      <button
                                        type="button"
                                        className="btn btn-link btn-outline-primary p-1 px-2"
                                        title="View details"
                                        onClick={() => handleViewDetails(timeSheet.id)}
                                      >
                                        <FaEye />
                                      </button>

                                      <button
                                        type="button"
                                        className="btn btn-outline-success p-1 px-2"
                                        title="Approve"
                                        onClick={() => handleApprove(timeSheet)}
                                        disabled={timeSheet.status === 'Approved' || timeSheet.status === 'Rejected'}
                                      >
                                        <FaCheckCircle />
                                      </button>

                                      <button
                                        type="button"
                                        className="btn btn-outline-danger p-1 px-2"
                                        title="Reject"
                                        onClick={() => handleOpenRejectModal(timeSheet)}
                                        disabled={timeSheet.status === 'Approved' || timeSheet.status === 'Rejected'}
                                      >
                                        <FaTimesCircle />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Time Sheets Grid View */}
              {viewMode === 'grid' && (
                <div className="row g-3">
                  {isLoading ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-muted">Loading...</p>
                        </div>
                      </div>
                    </div>
                  ) : loadError ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-danger">{loadError}</p>
                        </div>
                      </div>
                    </div>
                  ) : filteredTimeSheets.length === 0 ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-muted">No time sheets found</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    filteredTimeSheets.map((timeSheet) => {
                      const jobType = getJobTypeLabel(timeSheet.jobDurationType);
                      const plannedStart = getPlannedStart(timeSheet);
                      const plannedEnd = getPlannedEnd(timeSheet);
                      const actualStart = getActualStart(timeSheet);
                      const actualEnd = getActualEnd(timeSheet);

                      return (
                        <div key={timeSheet.id} className="col-12 col-md-6 col-lg-4">
                          <div className="card h-100 opportunity-card modern-card">
                            <div className="card-body d-flex flex-column p-0">
                              {/* Header */}
                              <div className="card-header-section">
                                <h5 className="card-position-title">{timeSheet.professionalName}</h5>
                                <span
                                  className={`badge ${
                                    timeSheet.status === 'Approved'
                                      ? 'bg-success'
                                      : timeSheet.status === 'Rejected'
                                      ? 'bg-danger'
                                      : 'bg-warning text-dark'
                                  }`}
                                >
                                  {timeSheet.status}
                                </span>
                              </div>

                              {/* Info */}
                              <div className="card-info-section">
                                <div className="info-item">
                                  <div className="info-icon hospital-icon">
                                    <FaHospital />
                                  </div>
                                  <div className="info-content">
                                    <div className="info-label">Hospital</div>
                                    <div className="info-value">{timeSheet.hospitalName}</div>
                                  </div>
                                </div>

                                <div className="info-item">
                                  <div className="info-icon location-icon">
                                    <FaClock />
                                  </div>
                                  <div className="info-content">
                                    <div className="info-label">Job Type</div>
                                    <div className="info-value">{jobType}</div>
                                  </div>
                                </div>

                                <div className="info-item">
                                  <div className="info-icon location-icon">
                                    <FaCalendarAlt />
                                  </div>
                                  <div className="info-content">
                                    <div className="info-label">Planned</div>
                                    <div className="info-value">
                                      {formatDate(plannedStart)}
                                      {plannedEnd ? ` → ${formatDate(plannedEnd)}` : ''}
                                    </div>
                                  </div>
                                </div>

                                <div className="info-item">
                                  <div className="info-icon location-icon">
                                    <FaCalendarAlt />
                                  </div>
                                  <div className="info-content">
                                    <div className="info-label">Actual</div>
                                    <div className="info-value">
                                      {formatDate(actualStart)}
                                      {actualEnd ? ` → ${formatDate(actualEnd)}` : ''}
                                    </div>
                                  </div>
                                </div>

                                <div className="info-item">
                                  <div className="info-icon location-icon">
                                    <FaClock />
                                  </div>
                                  <div className="info-content">
                                    <div className="info-label">Duration</div>
                                    <div className="info-value">
                                      {jobType === 'Period'
                                        ? '-'
                                        : `${timeSheet.duration ?? '-'} hrs`}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Details */}
                              <div className="card-details-section">
                                <div className="detail-item">
                                  <FaUser className="detail-icon" />
                                  <div className="detail-content">
                                    <span className="detail-label">Posting</span>
                                    <span className="detail-value">{timeSheet.posting}</span>
                                  </div>
                                </div>

                                <div className="detail-item">
                                  <FaClock className="detail-icon" />
                                  <div className="detail-content">
                                    <span className="detail-label">Total Hours</span>
                                    <span className="detail-value">
                                      {timeSheet.totalHours ?? '-'} {timeSheet.totalHours != null ? 'hrs' : ''}
                                    </span>
                                  </div>
                                </div>

                                <div className="detail-item">
                                  <FaCalendarAlt className="detail-icon" />
                                  <div className="detail-content">
                                    <span className="detail-label">Submitted</span>
                                    <span className="detail-value">{formatDate(timeSheet.submittedDate)}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="card-footer-section p-3 border-top">
                                <div className="d-flex gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm flex-fill"
                                    onClick={() => handleViewDetails(timeSheet.id)}
                                  >
                                    <FaEye className="me-1" /> View Details
                                  </button>

                                  <button
                                    type="button"
                                    className="btn btn-outline-success btn-sm"
                                    onClick={() => handleApprove(timeSheet)}
                                    title="Approve"
                                    disabled={timeSheet.status === 'Approved' || timeSheet.status === 'Rejected'}
                                  >
                                    <FaCheckCircle />
                                  </button>

                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleOpenRejectModal(timeSheet)}
                                    title="Reject"
                                    disabled={timeSheet.status === 'Approved' || timeSheet.status === 'Rejected'}
                                  >
                                    <FaTimesCircle />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Time Sheet Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={handleCloseDetailsModal}
        centered
        size="lg"
        scrollable
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Time Sheet Details</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedTimeSheet && (
            <div className="row m-0">
              {/* Professional Name */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Professional Name</label>
                <p>{selectedTimeSheet.professionalName}</p>
              </div>

              {/* Hospital Name */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Hospital Name</label>
                <p>{selectedTimeSheet.hospitalName}</p>
              </div>

              {/* Posting */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Posting Requirement</label>
                <p>{selectedTimeSheet.posting}</p>
              </div>

              {/* Job Type */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Job Type</label>
                <p>{getJobTypeLabel(selectedTimeSheet.jobDurationType)}</p>
              </div>

              {/* Planned Start */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Start Date (Planned)</label>
                <p>
                  {formatDate(getPlannedStart(selectedTimeSheet))}
                  {getPlannedStart(selectedTimeSheet) ? `, ${formatTime(getPlannedStart(selectedTimeSheet))}` : ''}
                </p>
              </div>

              {/* Planned End */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">End Date (Planned)</label>
                <p>{formatDate(getPlannedEnd(selectedTimeSheet))}</p>
              </div>

              {/* Actual Start */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Actual Start Date</label>
                <p>
                  {formatDate(getActualStart(selectedTimeSheet))}
                  {getActualStart(selectedTimeSheet) ? `, ${formatTime(getActualStart(selectedTimeSheet))}` : ''}
                </p>
              </div>

              {/* Actual End */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Actual End Date</label>
                <p>
                  {formatDate(getActualEnd(selectedTimeSheet))}
                  {getActualEnd(selectedTimeSheet) ? `, ${formatTime(getActualEnd(selectedTimeSheet))}` : ''}
                </p>
              </div>

              {/* Duration */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Duration</label>
                <p>
                  {getJobTypeLabel(selectedTimeSheet.jobDurationType) === 'Period'
                    ? '-'
                    : `${selectedTimeSheet.duration ?? '-'} hrs`}
                </p>
              </div>

              {/* Total Hours */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Total Hours</label>
                <p>{selectedTimeSheet.totalHours ?? '-'} {selectedTimeSheet.totalHours != null ? 'hrs' : ''}</p>
              </div>

              {/* Total Amount */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Total Amount</label>
                <p>
                  {selectedTimeSheet.totalAmount != null
                    ? `Rs. ${formatINRNumber(selectedTimeSheet.totalAmount)}`
                    : '-'}
                </p>
              </div>

              {/* Submitted Date */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Submitted Date</label>
                <p>{formatDate(selectedTimeSheet.submittedDate)}</p>
              </div>

              {/* Status */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold mb-1">Status</label>
                <p>{renderStatusBadge(selectedTimeSheet.status)}</p>
              </div>

              {/* Remarks */}
              <div className="col-12 mb-3">
                <label className="form-label fw-bold mb-1">Remarks By Professional</label>
                <textarea
                  className="form-control"
                  rows="3"
                  readOnly
                  value={selectedTimeSheet.remarksByProfessional || ''}
                  placeholder="No remarks provided"
                />
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          {selectedTimeSheet && (
            <>
              <button
                className="btn btn-success me-2"
                type="button"
                onClick={() => handleApprove(selectedTimeSheet)}
                disabled={
                  isRejectingOrApproving ||
                  selectedTimeSheet.status === 'Approved' ||
                  selectedTimeSheet.status === 'Rejected'
                }
              >
                Approve
              </button>

              <button
                className="btn btn-danger me-2"
                type="button"
                onClick={() => handleOpenRejectModal(selectedTimeSheet)}
                disabled={
                  isRejectingOrApproving ||
                  selectedTimeSheet.status === 'Approved' ||
                  selectedTimeSheet.status === 'Rejected'
                }
              >
                Reject
              </button>
            </>
          )}

          <button type="button" className="btn btn-secondary" onClick={handleCloseDetailsModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Reject Timesheet Modal */}
      <Modal show={showRejectModal} onHide={handleCloseRejectModal} centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title className="h5">Reject Time Sheet</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3">
            <p className="text-muted mb-3">Please provide remarks explaining the reason for rejection.</p>
            <label htmlFor="rejectRemarks" className="form-label fw-bold">
              Remarks <span className="text-danger">*</span>
            </label>
            <textarea
              id="rejectRemarks"
              className="form-control"
              rows="5"
              placeholder="Enter remarks for rejection..."
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
              disabled={isRejectingOrApproving}
            />
            {rejectRemarks.trim() === '' && (
              <small className="text-danger d-block mt-2">Remarks are required</small>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={handleCloseRejectModal} disabled={isRejectingOrApproving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleConfirmReject}
            disabled={!rejectRemarks.trim() || isRejectingOrApproving}
          >
            {isRejectingOrApproving ? 'Rejecting...' : 'Confirm Rejection'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TimeSheetApproval;
